"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Link } from "@/i18n/navigation";
import { cachedFetch } from "@/lib/fetch-cache";
import { setBetaRenderVersion } from "@/lib/image-url";
import { useChannel } from "@/lib/api/prefix.client";
import { LANG_PREFIXES } from "@/lib/languages";
import { useT } from "@/lib/i18n";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
function stripSuffix(v: string): string {
  return v.replace(/-beta$/, "");
}

/** The channel pill: exactly two options, main and the
 * current beta ("beta v0.107.0"), toggling between / and /beta on the SAME
 * site. Replaces the old cross-site switcher that listed every archived beta
 * version on beta.spire-codex.com; per the migration plan only the newest
 * beta is supported, and the channel indicator lives here and in the
 * per-page beta banner, never next to the logo. */
export default function SiteSwitcher(t0) {
  const $ = _c(44);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    align: t2
  } = t1;
  const align = t2 === undefined ? "right" : t2;
  const t = useT();
  const [betaVersion, setBetaVersion] = useState(null);
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const onBeta = useChannel() === "beta";
  let t3;
  if ($[2] !== onBeta || $[3] !== pathname) {
    const parts = pathname.split("/");
    const langSeg = LANG_PREFIXES.has(parts[1]) ? 1 : 0;
    const restStart = 1 + langSeg + (onBeta ? 1 : 0);
    t3 = parts.slice(restStart).filter(Boolean).join("/");
    $[2] = onBeta;
    $[3] = pathname;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const rest = t3;
  const mainHref = rest ? `/${rest}` : "/";
  const betaHref = `/beta${rest ? `/${rest}` : ""}`;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      cachedFetch(`${API}/api/beta/version`).then(d => {
        setBetaVersion(d.beta_version);
        setBetaRenderVersion(d.render_version || d.beta_version);
      }).catch(_temp);
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== open) {
    t6 = () => {
      const handleClick = function handleClick(e) {
        if (open && menuRef.current && !menuRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    };
    t7 = [open];
    $[7] = open;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== betaVersion || $[11] !== t) {
    t8 = betaVersion ? t("beta {version}", {
      version: stripSuffix(betaVersion)
    }) : t("beta");
    $[10] = betaVersion;
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const betaLabel = t8;
  const buttonClasses = onBeta ? "h-9 px-3 min-w-[7.5rem] rounded-lg text-xs font-semibold bg-success/15 text-success border border-success/30 hover:bg-success/25" : "h-9 px-3 rounded-lg text-xs font-semibold bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] border border-[var(--accent-gold)]/30 hover:bg-[var(--accent-gold)]/25";
  let t9;
  if ($[13] !== open) {
    t9 = () => setOpen(!open);
    $[13] = open;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `inline-flex items-center gap-1.5 transition-colors ${buttonClasses}`;
  let t11;
  if ($[15] !== t) {
    t11 = t("Switch between main and beta content");
    $[15] = t;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== betaLabel || $[18] !== onBeta || $[19] !== t) {
    t12 = onBeta ? betaLabel : t("main");
    $[17] = betaLabel;
    $[18] = onBeta;
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <span>{t12}</span>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = `w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14) {
    t16 = <svg className={t14} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden={true}>{t15}</svg>;
    $[24] = t14;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== open || $[27] !== t10 || $[28] !== t11 || $[29] !== t13 || $[30] !== t16 || $[31] !== t9) {
    t17 = <button ref={buttonRef} onClick={t9} className={t10} aria-label={t11} aria-expanded={open}>{t13}{t16}</button>;
    $[26] = open;
    $[27] = t10;
    $[28] = t11;
    $[29] = t13;
    $[30] = t16;
    $[31] = t9;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== align || $[34] !== betaHref || $[35] !== betaLabel || $[36] !== mainHref || $[37] !== onBeta || $[38] !== open || $[39] !== t) {
    t18 = open && <div ref={menuRef} className={`absolute ${align === "left" ? "left-0" : "right-0"} top-full mt-2 w-56 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] shadow-xl shadow-scrim/30 z-50`}><div className="py-1">{onBeta ? <Link href={mainHref} onClick={() => setOpen(false)} className="flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium text-[var(--accent-gold)] transition-colors hover:bg-[var(--bg-card)]"><span>{t("main")}</span></Link> : <Link href={betaHref} onClick={() => setOpen(false)} className="flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium text-success transition-colors hover:bg-[var(--bg-card)] hover:text-success"><span>{betaLabel}</span><span className="text-xs">{t("what's new")}</span></Link>}</div></div>;
    $[33] = align;
    $[34] = betaHref;
    $[35] = betaLabel;
    $[36] = mainHref;
    $[37] = onBeta;
    $[38] = open;
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <div className="relative">{t17}{t18}</div>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  return t19;
}
function _temp() {}